// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Typography, Button, TextField } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import { IfEmptyThrowError, IsEmpty, IsValidHexColor } from '../../../common/checks';
import apiService from '../../../services.js/apiService';
import AreaMap from '../../areaMap/areaMap';
import { Trim } from '../../../common/util';
import Constants from '../../../common/constants';
import RadioBtnSelect from '../../common/radioBtnSelect';
import MapboundCallout from './MapboundCallout';

const GeographicBoundary = ({ match, pageInView }) => {
  const pageId = match.params.id;
  const defaultColour = pageInView && pageInView.type === Constants.PageType.AreaManagement ? 'FF6699' : 'FF0000';

  const [loading, setLoading] = useState(true);
  const [colour, setColour] = useState(defaultColour);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [reviewCalloutTitle, setReviewCalloutTitle] = useState('');
  const [reviewCalloutContent, setReviewCalloutContent] = useState('');
  const [coords, setCoords] = useState([]);
  const [calloutCoord, setCalloutCoord] = useState({});
  const [data, setData] = useState(null);
  const [showDealReviewInBound, setShowDealReviewInBound] = useState(null);
  const [calloutMapBound, setCalloutMapBound] = useState([]);

  const getGeographicBoundaryDataHandler = async () => {
    try {
      setLoading(true);
      const mapBound = await apiService.getMapBound(pageId);

      if (mapBound) {
        setColour(mapBound.colour);
        setTitle(mapBound.calloutTitle || '');
        setContent(mapBound.calloutContent || '');
        setReviewCalloutTitle(mapBound.reviewCalloutTitle || '');
        setReviewCalloutContent(mapBound.reviewCalloutContent || '');
        setCoords(mapBound.coords);
        setCalloutCoord(mapBound.calloutCoord);
        setShowDealReviewInBound(mapBound.showDealReviewInBound ? 'true' : 'false');
        setData(mapBound);
      }
    } catch (error) {
      toast.error('Error while fetching data for partner buildings');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveHandler = async () => {
    try {
      IfEmptyThrowError(coords, 'Please select region in map as geographic boundary');

      setLoading(true);
      const trimmedCalloutTitle = Trim(title);
      const trimmedReviewCalloutTitle = Trim(reviewCalloutTitle);
      const rCalloutContent = Trim(reviewCalloutContent);
      const calloutContent = Trim(content);

      if (trimmedCalloutTitle.split('\n').length > 2 || trimmedReviewCalloutTitle.split('\n').length > 2) {
        return toast.error('Callout title should not exceed 2 lines');
      }

      const payload = {
        pageId:                Number(pageId),
        coords,
        colour:                IsValidHexColor(colour) ? colour : defaultColour,
        calloutTitle:          title,
        calloutContent,
        reviewCalloutTitle,
        reviewCalloutContent:  rCalloutContent,
        calloutCoord,
        showDealReviewInBound: showDealReviewInBound === 'true',
      };

      if (IsEmpty(data)) { // CREATE
        await apiService.createMapBound(pageId, payload);
        toast.success('Geographic boundary successfully created');
      } else { // UPDATE
        payload.id = data.id;
        await apiService.updateMapBound(pageId, payload);
        toast.success('Geographic boundary successfully updated');
      }

      // fetch latest data
      await getGeographicBoundaryDataHandler();
    } catch (error) {
      toast.error(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGeographicBoundaryDataHandler();
  }, []);

  useEffect(() => {
    setCalloutMapBound([...coords]);
  }, [coords]);

  return (
    <>
      {loading && <OverLoading />}
      <div className="container">
        <div className="w-100 mb-3">
          <Typography variant="h5" color="inherit" className="mb-3">
            Geographic Boundary
          </Typography>

          { pageInView && pageInView.type === Constants.PageType.AreaManagement && (
            <>
              <div>
                <Typography variant="h6" color="inherit" className="">
                  Boundary colour
                </Typography>
                <Typography variant="subtitle1" color="inherit" className="">
                  Set the colour that will be displayed in mobile app under Food Reviews and Deals Maps
                </Typography>

                <div className="mt-2 d-flex align-items-center">
                  <div className="d-flex align-items-center">
                    <span style={{ fontWeight: 'bold' }}>#</span>
                    <TextField
                      className="ml-1 mr-4 p-2"
                      variant="outlined"
                      style={{ width: '110px' }}
                      inputProps={{ style: { fontSize: '13px', padding: '10px 14px' } }}
                      value={colour}
                      onChange={(e) => setColour(e.target.value)}
                    />
                  </div>
                  { IsValidHexColor(colour) && <span style={{ display: 'block', width: '140px', height: '35px', backgroundColor: `#${colour}` }} /> }
                </div>
              </div>
              <hr />
            </>
          ) }

          { !loading && (
            <div>
              <Typography variant="h6" color="inherit" className="">
                Geographic Boundary
              </Typography>
              <AreaMap
                style={{ padding: 0 }}
                buttonContainerStyles={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
                buttonSubContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
                Heading={() => (
                  <Typography variant="subtitle1" color="inherit">
                    Select region in map as geographic boundary for this page
                  </Typography>
                )}
                colour={IsValidHexColor(colour) ? `#${colour}` : ''}
                defaultColour={`#${defaultColour}`}
                setMapCoor={(c) => {
                  setCoords([...c]);
                }}
                mapCoordinates={coords}
              />

              { pageInView && pageInView.type === Constants.PageType.AreaManagement && (
                <div className="mt-4">
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Enable search Deal and Review under region indicated above</Typography>
                  <RadioBtnSelect
                    className="ml-3"
                    value={showDealReviewInBound}
                    options={[
                      {  label: 'No',  value: 'false' },
                      {  label: 'Yes  (Note: This will override partner buildings)', value: 'true' },
                    ]}
                    onChange={(value) => setShowDealReviewInBound(value)}
                  />
                </div>
              )}
            </div>
          )}
          <hr />

          { pageInView && pageInView.type === Constants.PageType.AreaManagement && (
            <>
              <MapboundCallout
                heading="Deals Map"
                title={title}
                content={content}
                setTitle={setTitle}
                setContent={setContent}
              />
              <MapboundCallout
                heading="Food Reviews Map"
                title={reviewCalloutTitle}
                content={reviewCalloutContent}
                setTitle={setReviewCalloutTitle}
                setContent={setReviewCalloutContent}
              />
              <hr />

              { !loading && (
                <div className="mt-3">
                  <Typography variant="h6" color="inherit" className="">
                    Callout Placement
                  </Typography>
                  <AreaMap
                    areaType={pageInView.type}
                    style={{ padding: 0 }}
                    buttonContainerStyles={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
                    buttonSubContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
                    Heading={() => (
                      <Typography variant="subtitle1" color="inherit">
                        Select the callout's position in the map for this page
                      </Typography>
                    )}
                    colour={IsValidHexColor(colour) ? `#${colour}` : ''}
                    defaultColour={`#${defaultColour}`}
                    isCalloutCoord
                    setCalloutCoord={(c) => setCalloutCoord(c)}
                    calloutCoord={calloutCoord}
                    title={title}
                    content={content}
                    mapCoordinates={calloutMapBound}
                  />
                </div>
              )}
            </>
          )}

          <div className="d-flex mt-3 mb-3">
            <Button onClick={onSaveHandler} variant="contained" color="primary">
              {!IsEmpty(data) ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  pageInView: state.admin.page.pageInView,
});

export default connect(mapStateToProps)(GeographicBoundary);

GeographicBoundary.propTypes = {
  match:      PropTypes.shape(Object).isRequired,
  pageInView: PropTypes.shape(Object),
};

GeographicBoundary.defaultProps = {
  pageInView: {},
};
